import { useState } from 'react';
import { Bar, BarProps, VictoryPortal } from 'victory';
import { BankHistory } from '@algoan/dashboard/model';
import { chartPadding } from './bank-history-chart';
import { getBarAlignmentAndWidth, getBarColor, getBarVisibility } from './bank-history-chart-utils';
import { BalanceDifferenceTooltip, tooltipWidth } from './balance-difference-tooltip';
import { Triangle, triangleWidth, triangleY } from './triangle';
import { RangeStatus, useBankHistorySelection } from './use-bank-history-selection';

export const activeBarBonusWidth = 1;

interface BarWithTooltipProps extends BarProps {
  height?: number;
}

export const BarWithTooltip: React.FC<BarWithTooltipProps> = ({ ...props }) => {
  const [hovered, setHovered] = useState(false);
  const { endData, originData, rangeStatus, setOriginData, setEndData, updateOriginData } = useBankHistorySelection();

  const isSelected = originData?.date === props.datum.date || endData?.date === props.datum.date;

  const isStatusIdle = rangeStatus === RangeStatus.IDLE;
  const isUpdatingEndDate = rangeStatus === RangeStatus.SELECT_END_DATA || rangeStatus === RangeStatus.START_UPDATE_END;
  const isUpdatingOriginDate =
    rangeStatus === RangeStatus.SELECT_ORIGIN_DATA || rangeStatus === RangeStatus.START_UPDATE_ORIGIN;

  const extraProps = getBarAlignmentAndWidth(props);

  if (isSelected || hovered) {
    extraProps.barWidth += activeBarBonusWidth;
  }

  let x = (props.x ?? 0) - extraProps.barWidth / 2;

  if (extraProps.alignment === 'start') {
    x += extraProps.barWidth / 2;
  }

  if (extraProps.alignment === 'end') {
    x -= extraProps.barWidth / 2;
  }

  const cursor = isStatusIdle ? 'pointer' : 'inherit';

  return (
    <g
      cursor={cursor}
      onMouseOver={() => {
        setHovered(true);
        isUpdatingOriginDate && updateOriginData({ index: props.index, ...props.datum });
        isUpdatingEndDate && setEndData({ index: props.index, ...props.datum });
      }}
      onMouseOut={() => setHovered(false)}
      onMouseDown={() => isStatusIdle && setOriginData({ index: props.index, ...props.datum })}
    >
      {((hovered && !isSelected) || (isSelected && !endData)) && isStatusIdle && (
        <>
          <VictoryPortal>
            <BalanceDifferenceTooltip
              bankHistory={props.datum as BankHistory}
              x={Math.min(x, (props.width ?? 0) - tooltipWidth - chartPadding.right)}
              y={-40}
            />
          </VictoryPortal>
          <Triangle
            width={triangleWidth}
            x={extraProps.alignment === 'end' ? x - activeBarBonusWidth - extraProps.barWidth : x - activeBarBonusWidth}
            y={chartPadding.top - triangleY}
          />
        </>
      )}
      <rect
        fill={getBarColor({ isSelected, hovered })}
        height={(props.height ?? 0) - chartPadding.top - chartPadding.bottom}
        visibility={getBarVisibility({ isSelected, hovered })}
        width={extraProps.barWidth + 1}
        x={x - 0.5}
        y={chartPadding.top}
      />
      {props.datum.difference && <Bar {...props} {...extraProps} />}
    </g>
  );
};
